/**
 * CORE-BN — Front-of-House Attention Capacity · React binding
 *
 * Composes the read model, the CORE-BI policy and the pure estimate into
 * one hook, and exposes both halves of the capability's output:
 *
 *   `result` — the full, explainable estimate, for humans.
 *   `signal` — the narrow advisory contract, for planners.
 *
 * Determinism and the clock
 * -------------------------
 * The model never reads a clock; this hook does, exactly once per query,
 * and passes the window in explicitly. `refetchInterval` re-runs it on a
 * cadence rather than re-rendering against a moving `Date.now()`, so two
 * components mounted at the same moment see the same window and the same
 * verdict. The window is quantised to the refresh cadence so React Query
 * caching is not defeated by a key that changes every millisecond.
 *
 * Failure posture
 * ---------------
 * The hook never surfaces a thrown error as a capacity figure. Query
 * failure yields `signal.degradeToBaseline === true` and a `'unknown'`
 * state — the constitutionally required "plan as today" path.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFohAttentionObservations } from '@/lib/foh/attention/fetchFohAttentionObservations';
import {
  absentFohAttentionPlanningSignal,
  deriveFohAttentionPlanningSignal,
  estimateFohAttentionCapacity,
  explainFohAttentionResult,
  type FohAttentionCapacityResult,
  type FohAttentionPlanningSignal,
} from '@/lib/foh/attention';

/**
 * How often the estimate is recomputed. Front-of-house saturation moves
 * on the timescale of a custody dwell threshold (five minutes), so a
 * one-minute cadence is responsive without making the number twitch
 * while somebody is reading it.
 */
export const FOH_ATTENTION_REFRESH_MS = 60_000;

/**
 * Round a timestamp down to the refresh cadence. Two callers within the
 * same minute produce an identical window, hence an identical query key,
 * hence one shared result.
 */
export function quantizeToRefreshBucket(
  nowMs: number,
  bucketMs: number = FOH_ATTENTION_REFRESH_MS,
): number {
  if (!Number.isFinite(nowMs) || bucketMs <= 0) return nowMs;
  return Math.floor(nowMs / bucketMs) * bucketMs;
}

/**
 * Derive the estimate window from a quantised "now" and a length in
 * minutes. Exported so tests and future planners can build the same
 * window the hook uses without duplicating the arithmetic.
 */
export function buildFohAttentionWindow(
  quantizedNowMs: number,
  windowMinutes: number,
): { windowStartIso: string; windowEndIso: string } {
  const start = new Date(quantizedNowMs);
  const end = new Date(quantizedNowMs + windowMinutes * 60_000);
  return { windowStartIso: start.toISOString(), windowEndIso: end.toISOString() };
}

export interface UseFohAttentionCapacityArgs {
  shopId: string | null | undefined;
  /** Master gate. When false the hook is inert and the signal is absent. */
  enabled: boolean;
  /**
   * Window length override in minutes. Omit to use the shop's configured
   * window, falling back to the conservative 30-minute default.
   *
   * Note the ordering constraint: the shop's configured window lives in
   * the data this hook fetches, so the FIRST request must use some
   * window. It uses the default, then honours the shop's setting on the
   * subsequent refresh. Passing this argument removes the round trip.
   */
  windowMinutesOverride?: number;
}

export interface UseFohAttentionCapacityValue {
  /** `null` until the first successful fetch. */
  result: FohAttentionCapacityResult | null;
  /** Never `null`. Absent/failed states resolve to the degrade-to-baseline signal. */
  signal: FohAttentionPlanningSignal;
  /** One shop-scoped sentence. Never names a person. */
  summary: string;
  isLoading: boolean;
  isError: boolean;
}

const DEFAULT_WINDOW_MINUTES = 30;

export function useFohAttentionCapacity(
  args: UseFohAttentionCapacityArgs,
): UseFohAttentionCapacityValue {
  const { shopId, enabled, windowMinutesOverride } = args;
  const windowMinutes =
    typeof windowMinutesOverride === 'number' &&
    Number.isFinite(windowMinutesOverride) &&
    windowMinutesOverride > 0
      ? windowMinutesOverride
      : DEFAULT_WINDOW_MINUTES;

  const bucketMs = quantizeToRefreshBucket(Date.now());
  const { windowStartIso, windowEndIso } = useMemo(
    () => buildFohAttentionWindow(bucketMs, windowMinutes),
    [bucketMs, windowMinutes],
  );

  const query = useQuery({
    queryKey: ['core-bn-foh-attention', shopId, windowStartIso, windowMinutes],
    enabled: enabled && !!shopId,
    retry: false,
    refetchInterval: enabled && shopId ? FOH_ATTENTION_REFRESH_MS : false,
    staleTime: FOH_ATTENTION_REFRESH_MS,
    queryFn: async (): Promise<FohAttentionCapacityResult> => {
      const { observations, policy } = await fetchFohAttentionObservations({
        shopId: shopId as string,
        windowStartIso,
        windowEndIso,
      });
      return estimateFohAttentionCapacity({
        observations,
        policy,
        expectedShopId: shopId as string,
      });
    },
  });

  return useMemo(() => {
    const result = query.data ?? null;
    if (!result) {
      const signal = absentFohAttentionPlanningSignal(
        shopId ?? '',
        windowStartIso,
        windowEndIso,
      );
      return {
        result: null,
        signal,
        summary: signal.summary,
        isLoading: query.isLoading,
        isError: query.isError,
      };
    }
    const summary = explainFohAttentionResult(result);
    return {
      result,
      signal: deriveFohAttentionPlanningSignal(result, summary),
      summary,
      isLoading: query.isLoading,
      isError: query.isError,
    };
  }, [query.data, query.isLoading, query.isError, shopId, windowStartIso, windowEndIso]);
}
